import { ChangeDetectionStrategy, Component, EventEmitter, input, InputSignal, OnDestroy, OnInit, Output, signal, ViewChild, WritableSignal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DemandAdditionalCostType, DemandItemAdditionalCost } from '../../../types/IDemandItemAdditionalCost';
import {
    AccountingChange,
    AccountingSelectionV2Component,
    AccountingSelectionInput,
    CustomAccountingInput,
    AccountingSelectionModule,
    ItemAccountingDataService,
} from '@lib/accounting';
import { firstValueFrom } from 'rxjs';
import { IimDecimalInputV2Component, IimSpinnerModule } from '@iim-angular/components';
import { TranslateModule } from '@ngx-translate/core';
import { UnitModule } from '@lib/unit';
import { CopyToClipboardModule, DebugModule } from '@lib/core';
import { DisplayCurrencyModule } from '@lib/util';

@Component({
    selector: 'demand-additional-costs',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        IimDecimalInputV2Component,
        TranslateModule,
        UnitModule,
        AccountingSelectionModule,
        IimSpinnerModule,
        CopyToClipboardModule,
        DebugModule,
        DisplayCurrencyModule,
    ],
    templateUrl: './demand-additional-cost.component.html',
    styleUrl: './demand-additional-cost.component.scss'
})
export class DemandAdditionalCostComponent implements OnInit, OnDestroy {
    private readonly _itemAccountingService = inject(ItemAccountingDataService);

    @ViewChild(AccountingSelectionV2Component) accountingSelectionComponent?: AccountingSelectionV2Component;

    additionalCost: InputSignal<DemandItemAdditionalCost> = input.required();
    demandId: InputSignal<string> = input.required();
    isReadonly: InputSignal<boolean> = input.required();
    taskId: InputSignal<string> = input('');
    companyCodeId: InputSignal<string | null> = input(null);

    accountings: WritableSignal<AccountingSelectionInput> = signal(new AccountingSelectionInput());

    @Output() accountingsInitialized = new EventEmitter<void>();
    @Output() validationChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() initialized: EventEmitter<void> = new EventEmitter<void>();
    @Output() destroyed: EventEmitter<void> = new EventEmitter<void>();

    AdditionalCostType = DemandAdditionalCostType;

    public isInitialized = false;

    ngOnInit(): void {
        this.fetchAccountings();
    }

    ngOnDestroy(): void {
        this.destroyed.emit();
    }

    public refreshAccountingOptions(): void {
        if (this.accountingSelectionComponent) {
            this.accountingSelectionComponent.refreshOptions();
        }
    }

    public async onAccountingChange(changeEvent: AccountingChange): Promise<void> {
        await firstValueFrom(
            this._itemAccountingService.saveAccountingChange(this.demandId(), this.additionalCost().id, changeEvent),
        );

        const oldAccountings = this.accountings();
        const newAccountings = oldAccountings.applyEvent(changeEvent);
        this.accountings.set(newAccountings);
    }

    public async clearAccountingsForCompanyCodeChange(): Promise<void> {
        const current = this.accountings();
        const updates: Promise<void>[] = [];
        if (current.costCenterKey) {
            updates.push(
                firstValueFrom(
                    this._itemAccountingService.updateCostCenterAccount(
                        this.demandId(),
                        this.additionalCost().id,
                        '',
                    ),
                ),
            );
        }
        if (current.generalLedgerAccountKey) {
            updates.push(
                firstValueFrom(
                    this._itemAccountingService.updateGeneralLedgerAccount(
                        this.demandId(),
                        this.additionalCost().id,
                        '',
                    ),
                ),
            );
        }
        if (current.projectAccountKey) {
            updates.push(
                firstValueFrom(
                    this._itemAccountingService.updateProject(
                        this.demandId(),
                        this.additionalCost().id,
                        '',
                    ),
                ),
            );
        }
        if (updates.length) {
            await Promise.all(updates);
        }
        this.accountings.set(
            new AccountingSelectionInput(
                '',
                '',
                '',
                current.customAccountAssignments.map((c) => new CustomAccountingInput(c.typeId, c.key)),
            ),
        );
    }

    private fetchAccountings(): void {
        this._itemAccountingService
            .retrieveAccountAssignments(this.demandId(), this.additionalCost().id)
            .subscribe((accountings) => {
                this.accountings.set(AccountingSelectionInput.fromDto(accountings));
                this.isInitialized = true;
                this.initialized.emit();
            });
    }
}
